import type { Mode, Tie, Tournament } from './types';

export function uid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Plaatsingsvolgorde van een schema: 1-8, 4-5, 2-7, 3-6 enzovoort. */
export function plaatsingsVolgorde(aantal: number): number[] {
  let rij = [1];
  while (rij.length < aantal) {
    const spiegel = rij.length * 2 + 1;
    const volgende: number[] = [];
    for (const s of rij) volgende.push(s, spiegel - s);
    rij = volgende;
  }
  return rij;
}

export function rondeNaam(index: number, totaal: number): string {
  const vanAchter = totaal - index;
  if (vanAchter === 1) return 'Finale';
  if (vanAchter === 2) return 'Halve finale';
  if (vanAchter === 3) return 'Kwartfinale';
  return `Ronde ${index + 1}`;
}

/**
 * Bouwt een enkelvoudig afvalschema. Deelnemers worden geplaatst op rating —
 * de sterkste treft de zwakste — en het schema wordt aangevuld met byes tot de
 * eerstvolgende macht van twee. Byes gaan meteen door naar de volgende ronde.
 */
/*
 * Een plek in het schema heet een sleutel. Bij 1v1 is dat gewoon het id van de
 * speler; bij 2v2 is het "t0", "t1", ... — de plek in de ploegenlijst. De rest
 * van het schema hoeft het verschil niet te kennen.
 */
export function teamSleutel(index: number): string {
  return `t${index}`;
}

export function isTeamSleutel(sleutel: string): boolean {
  return /^t\d+$/.test(sleutel);
}

/** De speler of het duo achter een sleutel. */
export function spelersVan(toernooi: Tournament, sleutel: string | null): string[] {
  if (!sleutel) return [];
  if (!isTeamSleutel(sleutel)) return [sleutel];
  return toernooi.teams?.[Number(sleutel.slice(1))] ?? [];
}

/** Een sleutel als leesbare naam: één speler, of twee met een & ertussen. */
export function naamVan(
  toernooi: Tournament,
  sleutel: string | null,
  naamVanSpeler: (id: string) => string
): string {
  const ids = spelersVan(toernooi, sleutel);
  return ids.length ? ids.map(naamVanSpeler).join(' & ') : 'nog onbekend';
}

export function maakSchema(
  deelnemers: string[],
  rating: (id: string) => number,
  naam: string,
  pouleId: string | null,
  vorm?: { mode: Mode; teams: string[][] | null; participants: string[] }
): Tournament {
  const gesorteerd = deelnemers.slice().sort((a, b) => rating(b) - rating(a));
  let omvang = 2;
  while (omvang < gesorteerd.length) omvang *= 2;

  const plekken = plaatsingsVolgorde(omvang).map((plaatsing) =>
    plaatsing <= gesorteerd.length ? gesorteerd[plaatsing - 1] : null
  );

  const rondes: Tie[][] = [];
  let inRonde = omvang;
  let bron: (string | null)[] | null = plekken;
  while (inRonde >= 2) {
    const ronde: Tie[] = [];
    for (let i = 0; i < inRonde; i += 2) {
      ronde.push({
        id: uid(),
        a: bron ? bron[i] : null,
        b: bron ? bron[i + 1] : null,
        matchId: null,
        winnerId: null
      });
    }
    rondes.push(ronde);
    bron = null;
    inRonde /= 2;
  }

  let toernooi: Tournament = {
    id: uid(),
    pouleId,
    name: naam,
    size: omvang,
    mode: vorm?.mode ?? '1v1',
    teams: vorm?.teams ?? null,
    participants: vorm?.participants ?? gesorteerd,
    bracket: rondes,
    status: 'bezig',
    championId: null,
    createdAt: new Date().toISOString()
  };

  toernooi.bracket[0].forEach((partij, i) => {
    if (partij.a && !partij.b) toernooi = zetDoor(toernooi, 0, i, partij.a, null);
    else if (!partij.a && partij.b) toernooi = zetDoor(toernooi, 0, i, partij.b, null);
  });

  return toernooi;
}

/** Zet de winnaar door naar de volgende ronde. Geeft een nieuw toernooi terug. */
export function zetDoor(
  toernooi: Tournament,
  rondeIndex: number,
  partijIndex: number,
  winnaarId: string,
  matchId: string | null
): Tournament {
  const bracket = toernooi.bracket.map((ronde) => ronde.map((partij) => ({ ...partij })));

  bracket[rondeIndex][partijIndex].winnerId = winnaarId;
  if (matchId) bracket[rondeIndex][partijIndex].matchId = matchId;

  const volgende = bracket[rondeIndex + 1];
  if (!volgende) {
    return { ...toernooi, bracket, championId: winnaarId, status: 'klaar' };
  }

  const doel = volgende[Math.floor(partijIndex / 2)];
  if (partijIndex % 2 === 0) doel.a = winnaarId;
  else doel.b = winnaarId;

  return { ...toernooi, bracket };
}

export function gespeeldePartijen(toernooi: Tournament): number {
  return toernooi.bracket.reduce((n, ronde) => n + ronde.filter((p) => p.matchId).length, 0);
}
